import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { reportLabels } from '../../constants/reportLabels';
import type { ReportExportProgress, ReportPackage, ReportPackageKey } from '../../types';
import { summarizePackages } from '../../utils/reports';
import ReportPackageChips from './ReportPackageChips';

interface ExportReportDialogProps {
  open: boolean;
  packages: Record<ReportPackageKey, ReportPackage> | undefined;
  isGenerating: boolean;
  progress: ReportExportProgress | null;
  /** Charts are still being drawn: closing the dialog cancels the export. Once sent, the request runs to its end. */
  canCancel: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const TITLE_ID = 'export-report-dialog-title';

const progressLabel = (progress: ReportExportProgress | null, isGenerating: boolean): string => {
  if (!isGenerating) return '';
  if (progress?.phase === 'preparing') return reportLabels.preparing_charts;
  if (progress?.phase === 'rendering') return reportLabels.rendering_charts(Math.min(progress.done + 1, progress.total), progress.total);
  return reportLabels.generating;
};

const ExportReportDialog = ({ open, packages, isGenerating, progress, canCancel, onCancel, onConfirm }: ExportReportDialogProps) => {
  const summary = packages ? summarizePackages(packages) : null;
  const flagged = summary ? [...summary.stale, ...summary.missing] : [];
  const isCancellable = !isGenerating || canCancel;
  const rendering = isGenerating && progress?.phase === 'rendering' && progress.total > 0 ? progress : null;

  return (
    <Dialog open={open} onClose={isCancellable ? onCancel : undefined} aria-labelledby={TITLE_ID} fullWidth maxWidth="sm">
      <DialogTitle id={TITLE_ID}>{reportLabels.confirm_title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{reportLabels.confirm_intro}</DialogContentText>
        {packages && summary && (
          <Stack gap={2}>
            <section aria-label={reportLabels.confirm_included}>
              <Typography variant="subtitle2" component="h3" gutterBottom>
                {reportLabels.confirm_included}
              </Typography>
              <ReportPackageChips keys={summary.included} packages={packages} />
            </section>
            <section aria-label={reportLabels.confirm_flagged}>
              <Typography variant="subtitle2" component="h3" gutterBottom>
                {reportLabels.confirm_flagged}
              </Typography>
              {flagged.length > 0 ? (
                <ReportPackageChips keys={flagged} packages={packages} />
              ) : (
                <Typography variant="body2">{reportLabels.confirm_none_flagged}</Typography>
              )}
            </section>
          </Stack>
        )}
        <Typography role="status" variant="body2" sx={{ mt: 2, minHeight: '1.5em' }}>
          {progressLabel(progress, isGenerating)}
        </Typography>
        {isGenerating && (
          <LinearProgress
            aria-hidden
            variant={rendering ? 'determinate' : 'indeterminate'}
            value={rendering ? (rendering.done / rendering.total) * 100 : undefined}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={!isCancellable}>
          {reportLabels.cancel_button}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          loading={isGenerating}
          loadingPosition="start"
          disabled={!summary?.included.length}
          autoFocus
        >
          {reportLabels.confirm_button}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportReportDialog;
